import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Card,
  CardContent,
  LinearProgress,
  InputAdornment,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
  Clear as ClearIcon,
  Help as HelpIcon,
} from "@mui/icons-material";
import {
  fetchNewsById,
  createNews,
  updateNews,
  fetchNewsCategories,
  News,
  NewsCategory,
} from "../../services/api";

type NewsFormData = Omit<News, "id">;

const NewsForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<NewsFormData>({
    title: "",
    shortDescription: "",
    longDescription: "",
    active: true,
    newsCategoryId: undefined,
  });

  const [errors, setErrors] = useState<Partial<NewsFormData>>({});
  const [formTouched, setFormTouched] = useState<boolean>(false);
  const [charCount, setCharCount] = useState({
    title: 0,
    shortDescription: 0,
    longDescription: 0,
  });

  const {
    data: newsItem,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["news", id],
    queryFn: () => fetchNewsById(Number(id)),
    enabled: isEditMode,
  });

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["newsCategories"],
    queryFn: fetchNewsCategories,
  });

  useEffect(() => {
    if (newsItem) {
      setFormData(newsItem);
      setCharCount({
        title: newsItem.title.length,
        shortDescription: newsItem.shortDescription.length,
        longDescription: newsItem.longDescription.length,
      });
    }
  }, [newsItem]);

  const mutation = useMutation<News, Error, NewsFormData | News>({
    mutationFn: (data) => {
      if (isEditMode) {
        return updateNews({ ...data, id: Number(id) });
      }
      return createNews(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
      navigate("/");
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Partial<NewsFormData> = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (formData.title.length > 100)
      newErrors.title = "Title is too long (maximum 100 characters)";

    if (!formData.shortDescription.trim())
      newErrors.shortDescription = "Short description is required";
    if (formData.shortDescription.length > 200)
      newErrors.shortDescription =
        "Short description is too long (maximum 200 characters)";

    if (!formData.longDescription.trim())
      newErrors.longDescription = "Long description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      mutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Update character count
    if (type === "text" || type === "textarea") {
      setCharCount((prev) => ({
        ...prev,
        [name]: value.length,
      }));
    }

    // Mark form as touched
    if (!formTouched) {
      setFormTouched(true);
    }

    // Clear error when user types
    if (errors[name as keyof NewsFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCancel = () => {
    if (formTouched) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to leave?"
        )
      ) {
        navigate("/");
      }
    } else {
      navigate("/");
    }
  };

  // Reset form to original data
  const handleReset = () => {
    if (isEditMode && newsItem) {
      setFormData(newsItem);
      setCharCount({
        title: newsItem.title.length,
        shortDescription: newsItem.shortDescription.length,
        longDescription: newsItem.longDescription.length,
      });
    } else {
      setFormData({
        title: "",
        shortDescription: "",
        longDescription: "",
        active: true,
        newsCategoryId: undefined,
      });
      setCharCount({
        title: 0,
        shortDescription: 0,
        longDescription: 0,
      });
    }
    setErrors({});
  };

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", my: 4 }}>
        <LinearProgress />
        <Typography variant="subtitle1" sx={{ mt: 2, textAlign: "center" }}>
          Loading news article...
        </Typography>
      </Box>
    );
  }

  if (isError && error) {
    return (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => navigate("/")}>
            Back to News
          </Button>
        }
      >
        Error loading news: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
          News
        </Link>
        <Typography color="text.primary">
          {isEditMode ? `Edit: ${formData.title}` : "Create News"}
        </Typography>
      </Breadcrumbs>

      {/* Form Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "background.paper",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton sx={{ mr: 1 }} onClick={handleCancel}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            {isEditMode ? "Edit News Article" : "Create News Article"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={mutation.isPending}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={mutation.isPending}
            color="primary"
          >
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Paper>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error: {mutation.error.message}
        </Alert>
      )}

      {mutation.isPending && <LinearProgress sx={{ mb: 3 }} />}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Form Status */}
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={formData.active ? "Active" : "Inactive"}
                color={formData.active ? "success" : "default"}
                size="small"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={handleChange}
                    name="active"
                    color="primary"
                  />
                }
                label={formData.active ? "Published" : "Draft"}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {isEditMode ? "Editing existing article" : "Creating new article"}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box component="form" noValidate>
            <Grid container spacing={3}>
              {/* Category Field */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.newsCategoryId || ""}
                    label="Category"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        newsCategoryId: value ? Number(value) : undefined,
                      }));
                    }}
                    disabled={categoriesLoading}
                  >
                    <MenuItem value="">
                      <em>No Category</em>
                    </MenuItem>
                    {categories?.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Title Field */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="title"
                  label="Title"
                  name="title"
                  autoFocus
                  value={formData.title}
                  onChange={handleChange}
                  error={!!errors.title}
                  helperText={
                    errors.title || `${charCount.title}/100 characters`
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: formData.title ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, title: "" }));
                            setCharCount((prev) => ({ ...prev, title: 0 }));
                          }}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>

              {/* Short Description Field */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="shortDescription"
                  label="Short Description"
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleChange}
                  error={!!errors.shortDescription}
                  helperText={
                    errors.shortDescription ||
                    `${charCount.shortDescription}/200 characters - Will be displayed in list views`
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: formData.shortDescription ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              shortDescription: "",
                            }));
                            setCharCount((prev) => ({
                              ...prev,
                              shortDescription: 0,
                            }));
                          }}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>

              {/* Long Description Field */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="longDescription"
                  label="Full Content"
                  name="longDescription"
                  multiline
                  rows={8}
                  value={formData.longDescription}
                  onChange={handleChange}
                  error={!!errors.longDescription}
                  helperText={
                    errors.longDescription ||
                    "Full article content - supports markdown formatting"
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <InfoIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {charCount.longDescription} characters
                  </Typography>
                  <Tooltip title="This content will be displayed on the full article page">
                    <IconButton size="small">
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Bottom Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          startIcon={<ArrowBackIcon />}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          startIcon={<SaveIcon />}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Saving..." : isEditMode ? "Update" : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

export default NewsForm;
